import api, { getAuthHeaders } from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';

export const uploadService = {
  /**
   * Generate default batch name based on current time and user
   * @param {Object} user - User object with username
   * @returns {string} Default batch name
   */
  generateDefaultBatchName(user) {
    const now = new Date();
    const year = String(now.getFullYear()).slice(-2); // Last 2 digits of year
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const username = user?.username || 'user';
    return `${username}_${year}${month}${day}_${hours}${minutes}`;
  },

  /**
   * Prepare metadata for a single file upload
   * @param {Object} params - Parameters for metadata preparation
   * @returns {Object} Metadata object
   */
  prepareFileMetadata({
    uploadFile,
    previewItem,
    user,
    uploadProject,
    uploadTarget,
    effectiveBatchName
  }) {
    const currentUserName = user?.fullname || user?.username || '<none>';
    const sourcePath = uploadFile.webkitRelativePath || uploadFile.name;
    
    const metadata = {
      project: previewItem?.project || uploadProject || null,
      target: previewItem?.target || uploadTarget || null,
      mutation: previewItem?.mutation || uploadFile.mutation || null,
      subgroup: previewItem?.subgroup || uploadFile.subgroup || null,
      prep_workflow: previewItem?.prep_workflow || uploadFile.prep_workflow || null,
      batch: previewItem?.batch || effectiveBatchName,
      origin_type: 'custom',
      origin_name: previewItem?.origin_name || uploadFile.origin_name || null,
      ligand_name: previewItem?.ligand_name || uploadFile.ligand_name || null,
      ligand_tag: previewItem?.ligand_tag || uploadFile.ligand_tag || null,
      notes: previewItem?.notes || uploadFile.notes || null,
      author: previewItem?.author || uploadFile.author || currentUserName,
      source_path: sourcePath,
      tags: [],
      prepared_at: previewItem?.prepared_at || null
    };
    
    return metadata;
  },

  /**
   * Upload files with metadata and handle polling for processing
   * @param {Object} params - Upload parameters
   * @returns {Promise<Object>} API response
   */
  async uploadFiles({
    uploadedFiles,
    previewData,
    isPreviewMode,
    user,
    uploadProject,
    uploadTarget,
    uploadBatchName,
    onProgress
  }) {
    const effectiveBatchName = uploadBatchName || this.generateDefaultBatchName(user);
    
    if (!uploadBatchName) {
      console.log('Using default batch name:', effectiveBatchName);
    }

    // Prepare the data array and files for multipart/form-data
    const dataArray = [];
    const formData = new FormData();
    
    // Get the merged data for each file (including pattern-extracted values)
    uploadedFiles.forEach((uploadFile) => {
      // Find preview data if exists (from pattern extraction)
      const previewItem = isPreviewMode ? previewData.find(p => p.id === uploadFile.id) : null;
      
      // Prepare metadata for this file
      const metadata = this.prepareFileMetadata({
        uploadFile,
        previewItem,
        user,
        uploadProject,
        uploadTarget,
        effectiveBatchName
      });
      
      dataArray.push(metadata);
      
      // Add the file to FormData with source_path as the filename
      // This ensures the backend can match file.filename with metadata source_path
      const sourcePath = uploadFile.webkitRelativePath || uploadFile.name;
      formData.append('files', uploadFile.file, sourcePath);
    });
    
    // Add the JSON data as a single field
    formData.append('data', JSON.stringify(dataArray));
    
    // Submit to API with polling for 202 status
    let response;
    let pollCount = 0;
    const maxPolls = 300; // Maximum 300 polls (5 minutes with 1 second intervals)
    const pollInterval = 1000; // 1 second between polls
    
    // Initial upload request
    response = await api.post(API_ENDPOINTS.PREPS.BASE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...getAuthHeaders()
      }
    });
    
    console.log('Initial upload response status:', response.status);
    
    // If 202 Accepted, poll until completion
    while (response.status === 202 && pollCount < maxPolls) {
      pollCount++;
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress({
          status: 'processing',
          message: `Processing ${uploadedFiles.length} file${uploadedFiles.length > 1 ? 's' : ''}... (${pollCount * pollInterval / 1000}s)`,
          pollCount
        });
      }
      
      // Wait before polling again
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      
      // Poll with the same request
      response = await api.post(API_ENDPOINTS.PREPS.BASE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...getAuthHeaders()
        }
      });
      
      console.log(`Poll ${pollCount} response status:`, response.status);
    }
    
    // Check if we exceeded max polls
    if (pollCount >= maxPolls && response.status === 202) {
      throw new Error('Upload timeout: Processing took too long');
    }
    
    return response;
  }
};